import React, { useState } from 'react';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { mapMember } from '../members';
import { useUser } from '@/contexts/UserContext';
import { SectionLayout } from '@/components/section-layout';
import { Trash2, Download, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { trackClientCreated, trackClientUpdated, trackClientDeleted, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { ClientForm } from './components/ClientForm';
import { FloatingAlert } from '@/components/ui/floating-alert';
import type { InvitationNotice } from '../commercial-invitation';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  is_active: boolean;
  notes: string;
  created_at: string;
  attachment_count?: number;
}

export function ClientsPage() {
  const { companyId, user } = useUser();
  // The list: ONE server-driven query (search + sort + page) over the company's active clients -- the database searches, sorts and pages
  // everyone the user may see; each client's document counter comes with the row (hooks/useEntityList.ts).
  const list = useEntityList<any, Client>({
    entityType: 'client_member',
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'first_name', direction: 'asc', sortKey: 'name' },
    mapRow: mapMember,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [invitationNotice, setInvitationNotice] = useState<InvitationNotice | null>(null);

  const handleNewClient = () => {
    trackButtonClick('new_client_button', { context: 'client_management' });
    setEditingClient(null);
    setIsDialogOpen(true);
  };

  const handleEditClient = (client: Client) => {
    trackButtonClick('edit_client_button', { 
      context: 'client_management',
      client_id: client.id,
      client_name: `${client.first_name} ${client.last_name}`
    });
    setEditingClient(client);
    setIsDialogOpen(true);
  };

  const parseErrorMessage = (error: any): string => {
    // If it's already a user-friendly string, return it
    if (typeof error === 'string') {
      return error;
    }

    // Check for error object with message
    // API returns { success: false, error: "...", details: "...", code: "..." }
    // When thrown as Error, it becomes { message: "...", ... }
    // Supabase errors can be nested, so check both top-level and nested error objects
    const errorMessage = error?.message || error?.error || '';
    const errorDetails = error?.details || error?.error?.details || '';
    const errorCode = error?.code || error?.error?.code || '';

    // Handle duplicate email errors
    if (
      errorMessage.includes('email') && 
      (errorMessage.includes('unique') || errorMessage.includes('duplicate') || errorCode === '23505')
    ) {
      return 'This email address is already in use. Please use a different email.';
    }

    // Handle unique constraint violations
    if (errorCode === '23505') {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
        return 'This email address is already in use. Please use a different email.';
      }
      if (errorDetails?.includes('user_id') || errorDetails?.includes('company_id')) {
        return 'This client is already associated with this company.';
      }
      return 'This record already exists. Please check for duplicates.';
    }

    // Handle not-null constraint violations
    if (errorCode === '23502') {
      const fieldMatch = errorDetails?.match(/column "(\w+)" of relation/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const friendlyName = fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${friendlyName} is required.`;
      }
      return 'A required field is missing. Please check all required fields.';
    }

    // Handle foreign key violations
    if (errorCode === '23503') {
      return 'Invalid reference. Please check that all related records exist.';
    }

    // Handle check constraint violations
    if (errorCode === '23514') {
      if (errorMessage.includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (errorMessage.includes('status') || errorMessage.includes('role')) {
        return 'Invalid status or role value. Please select a valid option.';
      }
      return 'Invalid data format. Please check your input.';
    }

    // Handle specific error messages from API
    if (errorMessage.includes('user_id and company_id are required')) {
      return 'Session expired. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Failed to create user') || errorMessage.includes('Failed to update user')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        return 'Failed to save client. This email address is already in use. Please use a different email.';
      }
      return 'Failed to save client. Please check all fields and try again.';
    }

    // Handle generic "Failed to create user" or "Failed to update user" messages - check if it's a duplicate email
    if (errorMessage.includes('Failed to create') || errorMessage.includes('Failed to update')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
          return 'Failed to save client. This email address is already in use. Please use a different email.';
        }
      }
    }

    // Return the original error message if we can't parse it
    return errorMessage || 'An unexpected error occurred. Please try again.';
  };

  const handleSaveClient = async (formData: any): Promise<string | null> => {
    try {
      // 1. The acting user and the company come from the session (the server re-derives both)
      const userId = user?.id;
      if (!userId) throw new Error("User not logged in");
      if (!companyId) throw new Error("No active company found for this user");

      // 3. Insert or update client in users table
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.company_name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zip_code: formData.zip_code,
        // phase53_drop_obsolete_user_columns.sql (2026-09-09): users.status
        // used to be written here too, but nothing ever read it (confirmed
        // dead) -- `user_company_relation.is_active` remains the real
        // source of truth for active/inactive.
        lead_status: 'active_client', // Set as active client
        notes: formData.notes,
      };

      let savedId: string | null = editingClient ? editingClient.id : null;
      if (editingClient) {
        // Update existing user
        // Use generic API for user update
        const response = await fetch(getApiUrl(`api/entities/user/${editingClient.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            company_id: companyId,
            ...userData
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          const parsedError = parseErrorMessage(errorData);
          throw new Error(parsedError);
        }

        // Update the relationship's active status via the role-management
        // endpoint (PUT /api/user-role/:userId, PeopleService.changeRole) --
        // PUT /api/entities/user_company_relation/:id has never existed in
        // this backend (only POST is mounted), so this toggle never
        // actually worked before. changeRole() requires `role` even when
        // only is_active is changing; pass the client's own role unchanged.
        const relationUpdateResponse = await fetch(getApiUrl(`api/user-role/${editingClient.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role: 'client',
            is_active: formData.is_active,
          })
        });

        if (!relationUpdateResponse.ok) {
          throw new Error('Failed to update client relationship');
        }

        // Track successful client update with PostHog
        trackClientUpdated({
          client_id: editingClient.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zip_code: formData.zip_code,
          is_active: formData.is_active,
          company_id: companyId,
          updated_by: userId
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'client_update',
          success: true
        });
      } else {
        // ONE call: the server creates the person AND their client role in this company together (no half-created client if it fails)
        const response = await fetch(getApiUrl('api/entities/user'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            company_id: companyId,
            user_type: 'client',
            is_active: formData.is_active,
            ...userData
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          const parsedError = parseErrorMessage(errorData);
          throw new Error(parsedError);
        }

        const result = await response.json();
        const newUser = result.data;
        savedId = newUser?.id ?? null;

        // Track successful client creation with PostHog
        trackClientCreated({
          client_id: newUser.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zip_code: formData.zip_code,
          is_active: formData.is_active,
          company_id: companyId,
          created_by: userId
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'client_creation',
          success: true
        });
      }

      // Reset form + refresh clients
      setIsDialogOpen(false);
      setEditingClient(null);
      list.reload();
      return savedId;

    } catch (error: any) {
      console.error("Error saving client:", error);
      
      // Parse error message for better user experience
      const userFriendlyError = parseErrorMessage(error);
      
      // Track failed form submission with PostHog
      trackFormSubmission({
        form_type: editingClient ? 'client_update' : 'client_creation',
        success: false,
        error_message: userFriendlyError
      });
      
      throw error; // Re-throw so ClientForm can handle it
    }
  };


  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one client to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} client(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete clients.');
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const clientId of selectedRows) {
    try {
      // Soft delete via soft_delete_user() (see backend/migrations/
      // phase33_user_and_company_soft_delete.sql): one atomic call closes
      // the user_company_relation row AND stamps users.deleted_at/
      // deleted_by. Replaces the previous two-step PUT flow, which
      // targeted a PUT /api/entities/user_company_relation/:id route that
      // has never existed in this backend (only POST is mounted) and a
      // users.status column nothing ever reads -- confirmed dead, neither
      // step actually worked.
      const deleteResponse = await fetch(getApiUrl(`api/entities/user/${clientId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!deleteResponse.ok) {
        const errorData = await deleteResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete client');
      }

      // Track successful client deletion with PostHog
      trackClientDeleted({
          client_id: clientId,
        company_id: companyId,
          deleted_by: user.id
      });
      
        successCount++;
    } catch (error) {
      console.error('Error soft-deleting client:', error);
        errorCount++;
    }
    }

    list.reload();
    setSelectedRows([]);

    if (errorCount > 0) {
      alert(`Deleted ${successCount} client(s). ${errorCount} failed.`);
    } else {
      alert(`Successfully deleted ${successCount} client(s).`);
    }
  };

  // Export to CSV: the clients selected in the table, or -- with nothing selected -- EVERY client the current search matches,
  // read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'clients', 'clients');

  // Helper functions for formatting
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Map active/inactive to GenericTag status types
  const mapActiveStatusToTag = (isActive: boolean): 'active' | 'inactive' => {
    return isActive ? 'active' : 'inactive';
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<Client>[] = [
    {
      key: 'first_name',
      header: 'Name',
      width: '10rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => (
        <span className="font-medium">
          {row.first_name} {row.last_name}
        </span>
      ),
    },
    {
      key: 'company_name',
      header: 'Company',
      width: '8rem',
      sortable: true,
      sortKey: 'company_name',
      render: (row) => row.company_name || '-',
    },
    {
      key: 'email',
      header: 'Contact',
      width: '10rem',
      sortable: true,
      sortKey: 'email',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          {row.phone && <div className="text-sm text-gray-500">{row.phone}</div>}
        </div>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '7.5rem',
      sortable: true,
      sortKey: 'is_active',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapActiveStatusToTag(row.is_active)}
        />
      ),
    },
    {
      key: 'city',
      header: 'Location',
      width: '8rem',
      sortable: true,
      sortKey: 'city',
      render: (row) => row.city && row.state ? `${row.city}, ${row.state}` : '-',
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '5rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => (
        <span className="font-medium">{row.attachment_count || 0}</span>
      ),
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
  ];

  // Don't block page - let table show with skeleton rows

  return (
    <SectionLayout title="Clients">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={handleNewClient}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleEditClient}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No clients found"
        />

        {/* Add/Edit Client Form */}
        <ClientForm
          isOpen={isDialogOpen}
          client={editingClient}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingClient(null);
          }}
          onSave={handleSaveClient}
          onInvitation={setInvitationNotice}
        />
        {invitationNotice && <FloatingAlert title={invitationNotice.title} description={invitationNotice.description} variant={invitationNotice.variant} onDismiss={() => setInvitationNotice(null)} />}

      </div>
    </SectionLayout>
  );
} 
